/**
 * Wochenbalken der Probezeit (Spezifikation 2026-09-16, R7): Zielstunden je
 * Woche gestrichelt, gebuchte Stunden gefüllt, senkrechte Linie „bis hier gezählt".
 *
 * Bewusst reines SVG ohne Diagramm-Paket: 26 Balken, eine Achse, ein Strich.
 *
 * Das SVG wird auf die Breite des Fensters gezogen (`preserveAspectRatio="none"`).
 * Damit dabei kein Strich senkrecht dick und waagerecht haarfein wird, tragen alle
 * Striche `vectorEffect="non-scaling-stroke"` und ihre Stärke in Bildschirmpunkten.
 *
 * Jeder Text dieses Bausteins (Tooltip einer Woche, Titel, Legende, Beschriftung der Achse) kommt
 * aus `lib/probezeit-anzeige.ts`; hier steht nur, wo was gezeichnet wird.
 */
import type { ProbezeitReihenWoche } from "../../lib/api.js";
import { wochenBalkenTexte, wochenTooltip } from "../../lib/probezeit-anzeige.js";

/** Höhe der Zeichenfläche in Einheiten der viewBox (die Breite ist 100). */
const HOEHE = 120;
/** Luft über dem höchsten Balken, damit sein Strich nicht angeschnitten wird. */
const OBEN = 6;

export function WochenBalken({
  reihe,
  gezaehlteWoche,
}: {
  reihe: readonly ProbezeitReihenWoche[];
  /**
   * W: bis hierhin sind Wochen gezählt, dahinter steht die senkrechte Linie. Sie heißt
   * „bis hier gezählt" und nicht „heute": Eine Woche zählt erst 7 Tage nach ihrem Ende, der
   * heutige Tag liegt also weiter rechts.
   */
  gezaehlteWoche: number;
}) {
  if (reihe.length === 0) return null;
  const maxStunden = Math.max(...reihe.map((w) => Math.max(w.zielStunden, w.istStunden ?? 0)), 1);
  const breite = 100 / reihe.length;
  // Ein Balken steht auf dem unteren Rand der Zeichenfläche und wächst nach oben.
  const balkenHoehe = (h: number) => (h / maxStunden) * (HOEHE - OBEN);
  // Bei Woche 0 ist noch nichts gezählt, bei der letzten Woche ist alles gezählt: keine Linie.
  const linieSichtbar = gezaehlteWoche > 0 && gezaehlteWoche < reihe.length;
  const texte = wochenBalkenTexte(reihe.length, linieSichtbar);

  return (
    <div className="mt-3">
      <svg
        viewBox={`0 0 100 ${HOEHE}`}
        preserveAspectRatio="none"
        className="w-full"
        // Die Achse ist der untere Rand: Die Balken stehen genau darauf.
        style={{ height: 140, borderBottom: "1px solid var(--border-light)" }}
        role="img"
        aria-label={texte.beschreibung}
      >
        <title>{texte.titel}</title>
        {reihe.map((w, i) => {
          const x = i * breite + breite * 0.15;
          const zielHoehe = balkenHoehe(w.zielStunden);
          const istHoehe = w.istStunden === null ? 0 : balkenHoehe(w.istStunden);
          return (
            <g key={w.woche}>
              {w.istStunden !== null && (
                <rect
                  x={x}
                  y={HOEHE - istHoehe}
                  width={breite * 0.7}
                  height={istHoehe}
                  fill="var(--accent)"
                  opacity={0.85}
                />
              )}
              {/* Das Ziel liegt über dem Ist-Balken, sonst verschwindet es hinter jeder Woche über
                  Ziel. Dort, wo der Umriss auf dem gefüllten Balken (Akzentfarbe) liegt, braucht er
                  Kontrast: --text-secondary kam auf 2,09 : 1 (hell) und 1,73 : 1 (dunkel),
                  --text-primary kommt auf 4,69 : 1 und 3,45 : 1 (für Grafik nötig: 3 : 1). */}
              <rect
                x={x}
                y={HOEHE - zielHoehe}
                width={breite * 0.7}
                height={zielHoehe}
                fill="none"
                stroke="var(--text-primary)"
                strokeWidth={1}
                strokeDasharray="3 2"
                vectorEffect="non-scaling-stroke"
              />
              <rect x={i * breite} y={0} width={breite} height={HOEHE} fill="transparent">
                <title>{wochenTooltip(w)}</title>
              </rect>
            </g>
          );
        })}
        {linieSichtbar && (
          <line
            x1={gezaehlteWoche * breite}
            y1={0}
            x2={gezaehlteWoche * breite}
            y2={HOEHE}
            stroke="var(--text-muted)"
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      <div
        className="flex justify-between gap-2 text-[10px]"
        style={{ color: "var(--text-muted)" }}
      >
        <span>{texte.anfang}</span>
        <span>{texte.legende}</span>
        <span>{texte.ende}</span>
      </div>
    </div>
  );
}
